/**
 * Turbo Stream Extensions
 * 
 * Enhances Turbo Streams with custom actions and state-preserving morphing:
 * - Adds `partially_refresh`, `visit`, `frame`, `hide`, `show`, and `replaceHistoryState` actions
 * - Preserves attributes (`data-permanent-attributes`) and component state (`data-permanent-state`) during morphing
 * - Handles Turbo Frame progress bar visibility via `data-progress-bar`
 * - Uses Idiomorph for DOM morphing with custom beforeNodeMorphed callback
 * - Provides partiallyRefresh helper for reloading frames or fetching URLs
 */

import { StreamActions } from "@hotwired/turbo"
// import { emitCustomEvent } from './utils'
import { Idiomorph } from "idiomorph"
import { get } from '@rails/request.js'

// window.get = get

const beforeNodeMorphed = (oldNode, newNode) => {
  // console.log('oldNode', oldNode, 'new', newNode)
  //

  // Skip checks for non-Element nodes (like text nodes)
  if (!(newNode instanceof Element)) {
    return true
  }

  // respect the data-turbo-permanent
  if (newNode.dataset && newNode.dataset.turboPermanent) {
    return false
  }

  // Check if the new node has data-permanent-attributes
  if (newNode.dataset && newNode.dataset.permanentAttributes) {
    // Get the comma-separated list of attributes to preserve
    const permanentAttributes = newNode.dataset.permanentAttributes.split(',').map(attr => attr.trim())

    // Copy each specified attribute from the old node to the new node
    permanentAttributes.forEach(attrName => {
      if (oldNode.hasAttribute(attrName)) {
        const attrValue = oldNode.getAttribute(attrName)
        newNode.setAttribute(attrName, attrValue)
      }
    })
  }

  if (oldNode._dComponent) {
    const component = oldNode._dComponent

    // Check if the new node has data-permanent-state
    if (newNode.dataset && newNode.dataset.permanentState) {
      try {
        // Get the comma-separated list of state properties to preserve
        const permanentStateProps = newNode.dataset.permanentState.split(',').map(prop => prop.trim())

        // Get the old component's state
        const oldState = component.state

        // Parse the new state from d-state attribute
        let newState = {}
        if (newNode.hasAttribute('d-state')) {
          newState = JSON.parse(newNode.getAttribute('d-state'))
        }

        // Preserve specified state properties
        permanentStateProps.forEach(propName => {
          if (propName in oldState) {
            newState[propName] = oldState[propName]
          }
        })

        // console.log(newState, newNode.getAttribute('d-state'))

        // Update the d-state attribute with the merged state
        newNode.setAttribute('d-state', JSON.stringify(newState))

        // re-render since html is changed but the state might not be
        component.debouncedRender()
      } catch (error) {
        console.error('Error preserving component state:', error)
      }
    }
  }

  // replace instead of morph if the new node has data-replace-than-morph
  if (newNode.hasAttribute('data-replace-than-morph') && newNode.getAttribute('data-replace-than-morph') != 'false') {
    oldNode.replaceWith(document.importNode(newNode, true));
    return false; // Skip morphing
  }

  if (newNode.hasAttribute('data-permanent-on-morph') && newNode.getAttribute('data-permanent-on-morph') != 'false') {
    // If the new node has data-permanent-element, skip morphing
    return false // Skip morphing
  }

  return true
}

const afterNodeMorphed = () => {
  // Discovery narration uses custom buttons; never enable native controls on those elements.
  document.querySelectorAll('.audio-player .audio-element').forEach((media) => {
    media.controls = false
    media.removeAttribute('controls')
  })

  document.querySelectorAll('video[controls]').forEach((media) => {
    media.controls = true
  })
}


const morph = (node, newNode) => {
  Idiomorph.morph(node, newNode, {
    callbacks: {
      beforeNodeMorphed,
      afterNodeMorphed
    }
  })
}

document.addEventListener('turbo:before-morph-element', (event) => {
  // Respect the data-permanent-attributes and data-permanent-state attributes
  if (event.detail.newElement && event.target) {
    beforeNodeMorphed(event.target, event.detail.newElement)
  }
})

document.addEventListener('turbo:morph-element', () => {
  afterNodeMorphed()
})

const partiallyRefresh = (element) => {
  if (element.tagName?.toUpperCase() === 'TURBO-FRAME') {
    element.reload()
  } else {
    get(element.getAttribute('src'))
  }
}

StreamActions.partially_refresh = function() {
  const target = this.getAttribute('target')
  const selectors = target ? [`#${target}`] : this.getAttribute('targets').split(',')
  selectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(element => {
      partiallyRefresh(element)
    })
  })
}

// a visit action to visit other url
StreamActions.visit = function() {
  const url = this.getAttribute("url")
  Turbo.visit(url)
}

StreamActions.frame = function() {
  const target = this.getAttribute('target')
  const selectors = target ? [`#${target}`] : this.getAttribute('targets').split(',')
  const method = this.getAttribute("method");
  selectors.forEach(selector => {
    this.templateContent.querySelectorAll(selector).forEach(element => {
      document.querySelectorAll(selector).forEach(old => {
        if (method === "morph") {
          morph(old, element)
        } else {
          old.replaceWith(document.importNode(element, true));
        }
      })
    })
  })
}
StreamActions.hide = function() {
  const target = this.getAttribute('target')
  document.querySelector(`#${target}`)?.classList?.add('d-none')
}
StreamActions.show = function() {
  const target = this.getAttribute('target')
  document.querySelector(`#${target}`)?.classList?.remove('d-none')
}

StreamActions.replaceHistoryState = function() {
  const url = this.getAttribute("url")
  // window.history.replaceState({}, '', url)
  Turbo.navigator.history.replace({ href: url })
}

// FORCE PROGRSS BAR FOR TURBO FRAMES
// https://github.com/hotwired/turbo/issues/540
document.addEventListener("turbo:before-fetch-request", function(e) {
  // Only show the progress bar if the turbo frame has the data-progress-bar attribute
  if (e.target.tagName.toLowerCase() == 'turbo-frame' && (e.target.dataset.progressBar == '' || e.target.dataset.progressBar == 'true')) {
    Turbo.navigator.delegate.adapter.showProgressBar()
  }
})
document.addEventListener("turbo:frame-render", function(e) {
  if (e.target.tagName.toLowerCase() == 'turbo-frame' && (e.target.dataset.progressBar == '' || e.target.dataset.progressBar == 'true')) {
    Turbo.navigator.delegate.adapter.progressBar.hide()
  }
})

export { partiallyRefresh }
